import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  Divider,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';
import { isSsoEnabled } from '../config/ssoConfig';

const LoginPage: React.FC = () => {
  const { login, ssoLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState((location.state as any)?.ssoError || '');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Path the user was originally trying to reach before being bounced to
  // /login (only honored for full admins — see computePostLoginRedirect).
  const from = (location.state as any)?.from?.pathname || null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    try {
      setError('');
      setIsSubmitting(true);
      console.log('Attempting login with:', { email });
      
      // Get or generate device ID
      let deviceId = localStorage.getItem('device_id');
      if (!deviceId) {
        deviceId = `device_${Math.random().toString(36).substring(2, 15)}`;
        localStorage.setItem('device_id', deviceId);
      }
      
      await login(email, password);
      console.log('Login successful');

      // Read the freshly-stored user from authService — the AuthContext
      // value visible in this closure was captured at the previous render
      // and won't reflect the just-completed login. authService.login() has
      // already written the response to localStorage by the time await
      // resolves, so getCurrentUser() returns the new user synchronously.
      const freshUser = authService.getCurrentUser();

      if (freshUser?.passwordChangeRequired) {
        console.log('Password change required, redirecting to change password page');
        navigate('/change-password', { replace: true });
      } else {
        const target = authService.computePostLoginRedirect(from);
        console.log('Navigating to:', target);
        navigate(target, { replace: true });
      }
    } catch (err: any) {
      console.error('Login error details:', err);
      
      // Handle different error scenarios
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response:', {
          data: err.response.data,
          status: err.response.status,
          headers: err.response.headers
        });
        
        if (err.response.status === 401) {
          setError('Invalid email or password. Please try again.');
        } else if (err.response.data?.message) {
          setError(err.response.data.message);
        } else {
          setError(`Server error: ${err.response.status}`);
        }
      } else if (err.request) {
        // The request was made but no response was received
        console.error('Error request:', err.request);
        setError('No response from server. Please try again later.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error message:', err.message);
        setError(err.message || 'An unexpected error occurred');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Admin Login
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              inputProps={{ autoCapitalize: 'none' }}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              inputProps={{ autoCapitalize: 'none' }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              sx={{
                py: 1.5,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(99, 102, 241, 0.3)',
                },
                mb: 2,
              }}
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </Button>
            
            <Box sx={{ textAlign: 'center' }}>
              <Link
                component={RouterLink}
                to="/forgot-password"
                sx={{
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Forgot your password?
              </Link>
            </Box>
          </Box>

          {isSsoEnabled() && (
            <>
              <Divider sx={{ my: 3 }}>or</Divider>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => ssoLogin(from ?? undefined)}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                }}
              >
                Sign in with Nota SSO
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;
